/**
 * Übersetzt einen Fehler der MaStR-Abfrage (`/external/mastr-data`) in einen deutschsprachigen
 * Hinweistext für die Anzeige in der Fehler-Hinweiskarte.
 *
 * <p>Das Backend liefert im Fehlerfall eine RFC-7807-Problemantwort `{ status, title, detail }`,
 * deren `detail` mit einem stabilen Maschinen-Code beginnt (z. B. `MASTR_NOT_FOUND`). Dieser Code
 * (ersatzweise der HTTP-Status) bestimmt die Fehlerkategorie und damit Titel, Meldung und den
 * rollenabhängigen Handlungshinweis:
 *
 * <ul>
 *   <li><b>Konfigurationsfehler</b> (fehlende/ungültige Anbindung): Sachbearbeiter sollen ein
 *       Ticket für das Administrator-Team der Netzbetreiberplattform erstellen, Installateure sich
 *       an ihren Sachbearbeiter wenden.
 *   <li><b>Server-/Verbindungsfehler</b> (500/502/504): später erneut versuchen; bei anhaltendem
 *       Problem dieselben Kontakte wie bei Konfigurationsfehlern.
 *   <li><b>Nummer nicht gefunden / ungültige Eingabe</b>: reiner Nutzerhinweis ohne Kontakt-Hinweis.
 * </ul>
 */

/** Stabile Maschinen-Codes, die das Backend im `detail`-Feld voranstellt. */
export type MastrErrorCode =
  | 'MASTR_INVALID_INPUT'
  | 'MASTR_API_KEY_MISSING'
  | 'MASTR_INVALID_API_KEY'
  | 'MASTR_NOT_FOUND'
  | 'MASTR_NOT_CONFIGURED'
  | 'MASTR_UNREACHABLE'
  | 'MASTR_TIMEOUT'
  | 'MASTR_ERROR';

export interface MastrErrorHint {
  title: string;
  message: string;
  /** Rollenabhängiger Handlungshinweis (Ticket bzw. Sachbearbeiter). Fehlt bei reinen Nutzerfehlern. */
  hint?: string;
}

const MASTR_ERROR_CODES: readonly MastrErrorCode[] = [
  'MASTR_INVALID_INPUT',
  'MASTR_API_KEY_MISSING',
  'MASTR_INVALID_API_KEY',
  'MASTR_NOT_FOUND',
  'MASTR_NOT_CONFIGURED',
  'MASTR_UNREACHABLE',
  'MASTR_TIMEOUT',
  'MASTR_ERROR'
];

function isMastrErrorCode(value: string): value is MastrErrorCode {
  return (MASTR_ERROR_CODES as readonly string[]).includes(value);
}

/**
 * Extrahiert den führenden Maschinen-Code aus dem `detail`-Feld der Problemantwort
 * (z. B. `"MASTR_NOT_FOUND: ..."` → `"MASTR_NOT_FOUND"`). Liefert `null`, wenn kein bekannter
 * Code vorangestellt ist.
 */
export function mastrErrorCodeFromDetail(detail: string | null | undefined): MastrErrorCode | null {
  if (!detail) {
    return null;
  }
  const token = detail.trim().split(/[:\s]/)[0];
  return isMastrErrorCode(token) ? token : null;
}

type MastrErrorCategory = 'notFound' | 'invalidInput' | 'configuration' | 'transient' | 'unknown';

/** Rollenabhängiger Kontakt-Hinweis für Konfigurationsfehler. */
function contactHint(isInstaller: boolean): string {
  return isInstaller
    ? 'Bitte wenden Sie sich an Ihren zuständigen Sachbearbeiter.'
    : 'Bitte erstellen Sie ein Ticket für das Administrator-Team der Netzbetreiberplattform.';
}

/** Rollenabhängiger Kontakt-Hinweis für anhaltende Server-/Verbindungsfehler. */
function persistHint(isInstaller: boolean): string {
  return isInstaller
    ? 'Sollte das Problem weiterhin bestehen, wenden Sie sich bitte an Ihren zuständigen Sachbearbeiter.'
    : 'Sollte das Problem weiterhin bestehen, erstellen Sie bitte ein Ticket für das Administrator-Team der Netzbetreiberplattform.';
}

/** Bestimmt die Fehlerkategorie primär über den Code, ersatzweise über den HTTP-Status. */
function categorize(
  code: MastrErrorCode | null | undefined,
  status: number | null | undefined
): MastrErrorCategory {
  switch (code) {
    case 'MASTR_NOT_FOUND':
      return 'notFound';
    case 'MASTR_INVALID_INPUT':
      return 'invalidInput';
    case 'MASTR_API_KEY_MISSING':
    case 'MASTR_INVALID_API_KEY':
    case 'MASTR_NOT_CONFIGURED':
      return 'configuration';
    case 'MASTR_UNREACHABLE':
    case 'MASTR_TIMEOUT':
    case 'MASTR_ERROR':
      return 'transient';
    default:
      break;
  }
  switch (status) {
    case 404:
      return 'notFound';
    case 400:
      return 'invalidInput';
    case 501:
      return 'configuration';
    case 500:
    case 502:
    case 504:
      return 'transient';
    default:
      return 'unknown';
  }
}

const GENERIC_HINT: MastrErrorHint = {
  title: 'Fehler beim Abrufen der Daten',
  message: 'Die Daten konnten nicht vom Marktstammdatenregister abgerufen werden.'
};

/**
 * Ermittelt den anzuzeigenden Hinweis aus Fehler-Code (aus dem `detail`-Feld), HTTP-Status und der
 * Rolle des angemeldeten Benutzers (`isInstaller`).
 */
export function resolveMastrErrorHint(
  code: MastrErrorCode | null | undefined,
  status: number | null | undefined,
  isInstaller: boolean
): MastrErrorHint {
  switch (categorize(code, status)) {
    case 'notFound':
      return {
        title: 'MaStR-Nummer nicht gefunden',
        message:
          'Die angegebene MaStR-Nummer wurde im Marktstammdatenregister nicht gefunden. Bitte überprüfen Sie die Nummer.'
      };
    case 'invalidInput':
      return {
        title: 'Ungültige MaStR-Nummer',
        message: 'Die eingegebene MaStR-Nummer ist ungültig. Bitte überprüfen Sie die Eingabe.'
      };
    case 'configuration':
      return {
        title: 'MaStR-Anbindung nicht verfügbar',
        message: 'Die Anbindung an das Marktstammdatenregister ist derzeit nicht korrekt konfiguriert.',
        hint: contactHint(isInstaller)
      };
    case 'transient':
      return {
        title: 'Marktstammdatenregister zurzeit nicht erreichbar',
        message:
          'Die Daten konnten nicht vom Marktstammdatenregister abgerufen werden. Bitte versuchen Sie es später erneut.',
        hint: persistHint(isInstaller)
      };
    default:
      return GENERIC_HINT;
  }
}
